'use client'
import { Card, CardContent } from '@/components/ui/card'
import { useTranslations } from 'next-intl'
import { getIcon } from '@/lib/dynamic-icons'

interface FeatureBlock {
  iconName: string
  translationKey: string
  title?: string
  description?: string
}

interface FeaturesThreeBlockProps {
  namespace: string
  blocks: FeatureBlock[]
  variant?: 'default' | 'heroOverlay'
  useDirectData?: boolean
}

export default function FeaturesThreeBlock({ namespace, blocks, variant = 'default', useDirectData = false }: FeaturesThreeBlockProps) {
    // Use "Features" as fallback namespace when useDirectData is true to avoid missing translation errors
    const t = useTranslations(useDirectData ? 'Features' : namespace)

    // Cards only overlap the wide hero at lg+ (where the hero becomes aspect-video).
    // Below lg the hero is tall, so a positive top margin keeps the cards clear of it.
    const sectionClasses = variant === 'heroOverlay'
        ? "relative mt-14 pb-12 lg:-mt-16 lg:pb-28 z-10"
        : "relative mt-14 pb-16 lg:-mt-28 lg:pb-36 z-10"

    return (
        <section className={sectionClasses}>
            <div className="mx-auto max-w-7xl px-6">
                <div className={`grid grid-cols-1 gap-6 ${blocks.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-3'}`}>
                    {blocks.map((block, index) => {
                        const Icon = getIcon(block.iconName)
                        const title = useDirectData ? block.title : t(`${block.translationKey}.title`)
                        const description = useDirectData ? block.description : t(`${block.translationKey}.description`)

                        return (
                            <Card
                                key={index}
                                className="relative overflow-hidden bg-neutral-50/95 backdrop-blur-md border-neutral-200 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:-translate-y-1"
                            >
                                <CardContent className="pt-4 pb-6 md:pt-6 md:pb-8">
                                    {/* Title left, leather icon in the top-right corner. Inline columns
                                        (title flex-1, icon shrink-0) so text can never run under the icon. */}
                                    <div className="flex items-center justify-between gap-4">
                                        <h2 className="flex-1 font-[family-name:var(--font-bebas-neue)] text-[1.7rem] md:text-3xl font-semibold leading-[1.02] tracking-normal text-neutral-900">
                                            {title}
                                        </h2>
                                        <div className="flex aspect-square size-12 md:size-14 shrink-0 items-center justify-center rounded-full bg-[url('/leather.jpg')] bg-cover bg-center border border-[#2a1f10]/50 ring-1 ring-white/15 shadow-lg shadow-black/25">
                                            <Icon className="size-6 md:size-7 text-black" strokeWidth={1.5} />
                                        </div>
                                    </div>
                                    <p className="mt-3 max-w-[42ch] pr-4 text-[0.9375rem] leading-relaxed text-pretty text-neutral-600">
                                        {description}
                                    </p>
                                </CardContent>
                            </Card>
                        )
                    })}
                </div>
            </div>
        </section>
    )
}
